import { History, RemoveCircle, RemoveCircleOutline } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { MenuItem } from '@mui/material';
import dayjs from 'dayjs';
import React, { useState } from 'react';

import CAutocomplete from '../../components/CAutocomplete';
import CButton from '../../components/CButton';
import CButtonGroup from '../../components/CButtonGroup';
import CPageModule from '../../components/CPageModule';
import CustomDatePicker from '../../components/CustomDatePicker';
import CustomTextField from '../../components/CustomTextField';
import { type IMutableTableInternalState } from '../../components/IMutableTableInternalState';
import { type TPandasDataFrameDict } from '../../components/TPandasDataFrameDict';
import XDCheckbox from '../../components/forms/XDCheckbox';
import CustomSelectDropdown from '../../components/general/CustomSelectDropdown';
import Page from '../../components/layout/Page';
import { ETableIds } from '../../enums/ETableIds';
import { updateStateAttribute } from '../../helpers/helper';
import CMutableTable from './CMutableTable';

interface State {
  data: TPandasDataFrameDict;
  pseudoButtons: Partial<IMutableTableInternalState>;
}

export function PPlaygroundIndex() {
  const sampleDict = {
    'Dish Name': {
      '0': 'Apfelstrudel',
      '1': 'Banana Split'
    },
    'Created By': {
      '0': 'a',
      '1': 'b'
    },
    'Price (EUR)': {
      '0': '5',
      '1': '5'
    },
    'Expiry Date': { '0': new Date().getTime(), '1': new Date().getTime() },
    'Dish Type': {
      '0': 'dessert',
      '1': 'main'
    },
    'Vegan Dish': {
      '0': true,
      '1': true
    }
  };

  const users = [
    { key: 'a', label: 'Alex' },
    { key: 'b', label: 'Ben' },
    { key: 'c', label: 'Christian' }
  ];

  const [state, setState] = useState<State>({ data: sampleDict, pseudoButtons: {} });

  return (
    <Page title={'Playground'}>
      <div className={'p-4'}>
        <CPageModule fullWidth>
          <CMutableTable
            id={ETableIds.PLAYGROUND_SAMPLE}
            data={state.data}
            rowConfig={{
              body: {
                // onClick: (context) => {
                //   state.pseudoButtons.selectRow?.click?.(context.rowName);
                // },
                link: (context) => {
                  return { to: `/playground/${context.rowName}`, target: '_blank' };
                }
              }
            }}
            columnConfig={[
              {
                span: 1.5,
                body: {
                  render: (context) => (
                    <CustomTextField
                      value={context.value}
                      onChange={(ev) => {
                        context.onChange(ev.target.value);
                      }}
                      placeholder={'Dish Name'}
                      onClick={(ev) => {
                        ev.preventDefault();
                        ev.stopPropagation();
                      }}
                    />
                  )
                }
              },
              {
                span: 1,
                fixWidth: true,
                body: {
                  render: (context) => (
                    <CAutocomplete
                      options={users}
                      value={users.filter((e) => e.key === context.value)}
                      onChangeSingle={(option) => {
                        context.onChange?.(option.key);
                      }}
                      placeholder={'Owner'}
                      onClick={(ev) => {
                        ev.preventDefault();
                        ev.stopPropagation();
                      }}
                    />
                  ),
                  style: () => {
                    return {
                      overflow: 'visible'
                    };
                  }
                }
              },
              {
                span: 1,
                body: {
                  style: () => {
                    return {
                      textAlign: 'right'
                    };
                  },
                  render: (context) => (
                    <CustomTextField
                      value={context.value}
                      onChange={(ev) => {
                        context.onChange(ev.target.value);
                      }}
                      type={'number'}
                      placeholder={'Price (EUR)'}
                      onClick={(ev) => {
                        ev.preventDefault();
                        ev.stopPropagation();
                      }}
                    />
                  )
                }
              },
              {
                span: 1,
                body: {
                  style: () => {
                    return {
                      textAlign: 'right'
                    };
                  },
                  render: (context) => (
                    <div
                      onClick={(ev) => {
                        ev.preventDefault();
                        ev.stopPropagation();
                      }}
                    >
                      <CustomDatePicker
                        value={context.value ? dayjs(context.value as number) : undefined}
                        onChange={(ev) => {
                          context.onChange(dayjs(ev).unix() * 1000);
                        }}
                      />
                    </div>
                  )
                }
              },
              {
                span: 1,
                fixWidth: true,
                body: {
                  render: (context) => (
                    <CustomSelectDropdown
                      fullWidth
                      value={(context.value as string) ?? ''}
                      onChange={(ev) => {
                        context.onChange(ev.target.value);
                      }}
                      placeholder={'Dish Type'}
                      onClick={(ev) => {
                        ev.preventDefault();
                        ev.stopPropagation();
                      }}
                    >
                      <MenuItem value={'appetizer'}>Appetizer</MenuItem>
                      <MenuItem value={'main'}>Main</MenuItem>
                      <MenuItem value={'dessert'}>Dessert</MenuItem>
                    </CustomSelectDropdown>
                  )
                }
              },
              {
                span: 0.1,
                body: {
                  render: (context) => (
                    <XDCheckbox
                      checked={context.value as boolean}
                      onChange={(ev) => {
                        context.onChange(ev.target.checked);
                      }}
                    />
                  )
                }
              }
            ]}
            onSave={(data) => {
              updateStateAttribute<State>({ data }, setState);
            }}
            onUpdateInternalState={(pseudoButtons) => {
              updateStateAttribute<State>({ pseudoButtons }, setState);
            }}
            edgeColumnConfig={{
              prefix: {
                header: {
                  render: () => (
                    <XDCheckbox
                      checked={state.pseudoButtons.selectAllRow?.isTrue}
                      onChange={() => {
                        state.pseudoButtons.selectAllRow?.click?.();
                      }}
                    />
                  )
                },
                body: {
                  render: (context) => (
                    <XDCheckbox
                      checked={state.pseudoButtons.selectRow?.selected?.(context.rowName)}
                      onChange={() => {
                        state.pseudoButtons.selectRow?.click?.(context.rowName);
                      }}
                    />
                  )
                }
              },
              suffix: {
                body: {
                  render: (context) => (
                    <span className={'flex'}>
                      <CButton
                        icons={{ start: <History /> }}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          ev.preventDefault();
                          state.pseudoButtons.revertRow?.click?.(context.rowName);
                        }}
                        disabled={!state.pseudoButtons.revertRow?.enabled?.(context.rowName)}
                      />
                      <CButton
                        icons={{
                          start: state.pseudoButtons.deleteRow?.toggled(context.rowName) ? (
                            <RemoveCircle className={'text-red-700'} />
                          ) : (
                            <RemoveCircleOutline />
                          )
                        }}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          ev.preventDefault();
                          state.pseudoButtons.deleteRow?.click?.(context);
                        }}
                      />
                    </span>
                  )
                }
              }
            }}
            // renderCoordinates
          />
          <CButtonGroup style={{ justifyContent: 'end' }}>
            <CButton icons={{ start: <AddIcon /> }} onClick={state.pseudoButtons.addRow?.click}>
              Append Row
            </CButton>
            <CButton onClick={state.pseudoButtons.revertAll?.click} disabled={state.pseudoButtons.revertAll?.disabled}>
              Revert All Changes
            </CButton>
            <CButton onClick={state.pseudoButtons.save?.click} disabled={state.pseudoButtons.save?.disabled}>
              Save
            </CButton>
          </CButtonGroup>
          <CMutableTable
            onUpdateInternalState={() => {}}
            id={ETableIds.PLAYGROUND_SAMPLE_2}
            data={{ 'Dish Name': state.data['Dish Name'] }}
            rowConfig={{
              body: {
                link: () => {
                  return { to: 'https://www.nexdos.de' };
                }
              }
            }}
            columnConfig={[
              {
                body: {
                  render: (context) => (
                    <>
                      {(context.value as string)?.split(' ').map((v, ix) => (
                        <p key={ix}>{v}</p>
                      ))}
                    </>
                  )
                }
              }
            ]}
          />
        </CPageModule>
      </div>
    </Page>
  );
}
